import { Resource } from "@domain/base/Resource";
import { IWooCommerceConnectorRepository } from "@domain/connection/IMarketplaceRepository";
import { Connection } from "@domain/connection/models/Connection";
import { ConnectionMapper } from "../mappers/MarketplaceMapper";
import { WooCommerceConnectorDataSource } from "../datasources/WooCommerceConnectorDataSource";
import { InventorySyncEventItem } from "@domain/connection/models/InventorySyncEvent";
import { FullSyncJob } from "@domain/connection/models/ImportJob";

export class WooCommerceConnectorRepository implements IWooCommerceConnectorRepository {
    constructor(private dataSource: WooCommerceConnectorDataSource) { }

    async addWooCommerceConnection(url: string, consumerKey: string, consumerSecret: string): Promise<Resource<Connection>> {
        try {
            const data = await this.dataSource.addWooCommerceConnection(url, consumerKey, consumerSecret);
            return {
                status: 'success',
                data: ConnectionMapper.toConnectionModel(data)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to add WooCommerce connection'
                }
            };
        }
    }

    async syncAllOrders(connectionId: string): Promise<Resource<{ jobId: string }>> {
        try {
            const data = await this.dataSource.syncAllOrders(connectionId);
            return { status: 'success', data };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to sync WooCommerce orders' } };
        }
    }

    async syncNewOrders(connectionId: string): Promise<Resource<{ jobId: string }>> {
        try {
            const data = await this.dataSource.syncNewOrders(connectionId);
            return { status: 'success', data };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to sync WooCommerce new orders' } };
        }
    }

    async syncProducts(connectionId: string): Promise<Resource<void>> {
        try {
            await this.dataSource.syncProducts(connectionId);
            return { status: 'success', data: undefined };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to sync WooCommerce products' } };
        }
    }

    async fetchInventory(connectionId: string, page = 1): Promise<Resource<InventorySyncEventItem[]>> {
        try {
            const data = await this.dataSource.fetchInventory(connectionId, page);
            return { status: 'success', data };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to fetch WooCommerce inventory' } };
        }
    }

    async syncFull(connectionId: string): Promise<Resource<FullSyncJob[]>> {
        try {
            const data = await this.dataSource.syncFull(connectionId);
            return { status: 'success', data };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to start full sync' } };
        }
    }

    async reconnect(connectionId: string, url: string, consumerKey: string, consumerSecret: string): Promise<Resource<Connection>> {
        try {
            const data = await this.dataSource.reconnect(connectionId, url, consumerKey, consumerSecret);
            return { status: 'success', data: ConnectionMapper.toConnectionModel(data) };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to reconnect WooCommerce connection' } };
        }
    }
}
